import { DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import ExcelExportUtils from '@core/utils/excel-export.utils';
import RouteUtils from '@core/utils/route.utils';
import {
  DvDynamicFormElement,
  DvFilterRangeKeys,
  DvFormUtils,
  FormOf,
} from '@devinco/ui/legacy';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridContext } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { UpsertManyItemStatus } from '@shared/constants/upsert-many';
import { TranslateService } from '@ngx-translate/core';
import TripUtils from '@pages/trips-page/shared/trip.utils';
import { FilterInterval } from '@shared/components/edit-trip-filter-dialog/constants';
import { DataFilterCreateEditForm } from '@shared/components/edit-trip-filter-dialog/interfaces';
import { DataFilterElementsService } from '@shared/components/edit-trip-filter-dialog/services/data-filter-elements.service';
import {
  ApplyColumnStateParams,
  FillOperationParams,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowEditingStartedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { catchError, Observable, of, Subject, Subscription, take } from 'rxjs';
import { TripDto, TripUpsertManyResp } from './interfaces';
import { TripsGridService } from '@pages/trips-page/services/trips-grid.service';
import { ReportProgressService } from '@core/services/report-progress.service';
import { DialogService } from '@core/services/dialog.service';
import { DvDialogService2 } from '@devinco/ui/dds';

@Component({
  selector: 'app-trips-grid',
  templateUrl: './trips-grid.component.html',
  styleUrls: ['./trips-grid.component.scss'],
  providers: [GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripsGridComponent implements OnInit {
  private tripsGridService = inject(TripsGridService);
  private gridOptionsService = inject(GridOptionsService);
  private dataFilterElementsService = inject(DataFilterElementsService);
  private translate = inject(TranslateService);
  private rolesAndPermissionsService = inject(UserRolesAndPermissionsService);
  private dialog = inject(DialogService);
  private dialogService = inject(DvDialogService2);
  private api = inject(RestApiService);
  private reportProgressService = inject(ReportProgressService);
  private route = inject(ActivatedRoute);

  gridOptions: GridOptions<TripDto>;
  actions = signal<DvToolbarAction<TripDto>[]>([]);

  departmentDriverAndPeriodElements: DvDynamicFormElement[];
  monToWedElements: DvDynamicFormElement[];
  thuToSatElements: DvDynamicFormElement[];
  sundayElement: DvDynamicFormElement[];
  intervalElements: DvDynamicFormElement[];
  allElements: DvDynamicFormElement[];
  formGroup: FormGroup<FormOf<DataFilterCreateEditForm>>;
  selectedNode: IRowNode<TripDto> | undefined;
  excelLoadingTrigger$: Subject<boolean>;
  entity: typeof Entity = Entity;
  gridApi: GridApi | undefined;
  reportInProgressText$: Observable<string>;
  readonly customColumnState: ApplyColumnStateParams = {
    state: [
      {
        colId: 'StartTime',
        sort: 'desc',
      },
    ],
  };
  private destroyRef = inject(DestroyRef);
  private isPrivateTaxableSubscription$?: Subscription;
  private fillableTrips: {
    TripID: number;
    UserID?: number;
    Username?: string;
    Purpose?: string;
  }[] = [];

  @Input() selectedFilterName?: string;
  @Output() clearGridFiltersClicked = new EventEmitter<void>();

  ngOnInit(): void {
    this.rolesAndPermissionsService
      .hasPermissions([RolesAndPermissions.TripShowOnlyMine])
      .pipe(take(1))
      .subscribe(hasPermission => {
        this.setGridOptions();
        this.actions.set(
          this.tripsGridService.getGridActions(
            this.getExcelExportFileName,
            hasPermission
          )
        );
        this.setFormElements(hasPermission);
        this.setupEvents();
        this.excelLoadingTrigger$ = this.tripsGridService.excelLoadingTrigger$;
        this.reportInProgressText$ = this.reportProgressService.progressText$;
      });
  }

  onGridFiltersCleared(): void {
    this.formGroup.reset();
    this.gridOptions.context.extraFilter = TripUtils.getDefaultTripGridFilter();
    this.formGroup.controls.Period?.setValue({
      selectedFilter: {
        title: this.translate.instant(DvFilterRangeKeys.Today),
        filterKey: DvFilterRangeKeys.Today,
      },
    });
    this.formGroup.controls.Interval?.setValue(FilterInterval.None);
    this.formGroup.controls.IntervalFrom?.reset();
    this.formGroup.controls.IntervalTo?.reset();
    this.gridOptions.context.customFilter = undefined;
    this.clearGridFiltersClicked.emit();
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.selectedNode = api.getSelectedNodes()[0] as
      | IRowNode<TripDto>
      | undefined;

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'edit') {
          return { ...action, disabled: !this.selectedNode };
        }
        return action;
      })
    );
  }

  onGridReady(event: GridReadyEvent<TripDto, GridContext>): void {
    this.gridApi = event.api;
    this.formGroup.controls.Period?.setValue({
      selectedFilter: {
        filterKey: DvFilterRangeKeys.Today,
        title: this.translate.instant(DvFilterRangeKeys.Today),
      },
    });
    this.subscribeToGridDataLoaded(event.context);
  }

  onRowEditingStarted(event: RowEditingStartedEvent): void {
    this.isPrivateTaxableSubscription$?.unsubscribe();
    const { DistancePrivateTaxable, IsPrivateTaxable } = (
      event.context as GridContext
    ).formGroup!.controls;
    if (!IsPrivateTaxable.value) {
      DistancePrivateTaxable.disable();
    } else {
      DistancePrivateTaxable.enable();
      DistancePrivateTaxable.addValidators([
        Validators.max(event.data.Distance),
      ]);
    }

    this.isPrivateTaxableSubscription$ =
      IsPrivateTaxable.valueChanges.subscribe((value: boolean) => {
        if (value) {
          DistancePrivateTaxable.setValue(event.data.Distance);
          DistancePrivateTaxable.addValidators([
            Validators.max(event.data.Distance),
          ]);
          DistancePrivateTaxable.enable();
        } else {
          DistancePrivateTaxable.setValue(undefined);
          DistancePrivateTaxable.disable();
          DistancePrivateTaxable.setValidators([]);
        }
        DistancePrivateTaxable.markAsDirty();
      });
  }

  onFilterChanged(event: FilterChangedEvent): void {
    if (event.context.isInlineEditingInProgress$.getValue()) return;
    this.selectUrlRowOrFirst(event.api);
  }

  private selectUrlRowOrFirst(api: GridApi): void {
    const entityId = RouteUtils.getEntityId(this.route.firstChild);
    const urlNode = entityId ? api.getRowNode(entityId) : undefined;
    if (urlNode) {
      urlNode.setSelected(true, true);
      return;
    }
    const firstRow = api.getDisplayedRowAtIndex(0);
    if (firstRow?.id) {
      firstRow.setSelected(true, true);
    }
  }

  private setGridOptions(): void {
    this.gridOptions = {
      ...this.gridOptionsService.serverSide(
        this.tripsGridService.getColumnDefs(),
        this.entity.Trip,
        TripUtils.getDefaultTripGridFilter(),
        undefined,
        [
          'Distance',
          'DistancePrivateTaxable',
          'TripDurationHM',
          'CO2Emission',
          'KWh',
        ] satisfies (keyof TripDto)[]
      ),
      cacheBlockSize: 200,
      rowSelection: {
        mode: 'singleRow',
        checkboxes: false,
      },
      cellSelection: {
        handle: {
          mode: 'fill',
          direction: 'y',
          setFillValue: (params: FillOperationParams) =>
            this.fillOperationFn(params),
        },
      },
      suppressDragLeaveHidesColumns: true,
      enableCellEditingOnBackspace: true,
    };
  }

  private getExcelExportFileName = (): string => {
    return ExcelExportUtils.getCustomPeriodExcelFileName(
      this.formGroup.controls.Period?.value,
      this.selectedFilterName
    );
  };

  private setFormElements(hasPermission: boolean): void {
    this.departmentDriverAndPeriodElements = [
      ...this.dataFilterElementsService.departmentAndDriverElements(
        12,
        12,
        this.translate.instant('Trip.Filters.DepartmentAll'),
        this.translate.instant('Trip.Filters.DriverAll'),
        false,
        hasPermission
      ),
      this.dataFilterElementsService.getPeriodElements(
        12,
        this.translate.instant('Trip.Filters.PeriodAll'),
        false,
        1,
        true
      )[0],
    ];

    this.monToWedElements = this.dataFilterElementsService
      .getPeriodElements(undefined, undefined, undefined, 4)
      .slice(1, 4);

    this.thuToSatElements = this.dataFilterElementsService
      .getPeriodElements(undefined, undefined, undefined, 4)
      .slice(4, 7);

    this.sundayElement = this.dataFilterElementsService
      .getPeriodElements(undefined, undefined, undefined, 4)
      .slice(7, 8);

    this.intervalElements =
      this.dataFilterElementsService.getIntervalElements(4);

    this.allElements = [
      ...this.departmentDriverAndPeriodElements,
      ...this.monToWedElements,
      ...this.thuToSatElements,
      ...this.sundayElement,
      ...this.intervalElements,
    ];

    this.formGroup = DvFormUtils.createFormGroup(this.allElements);
  }

  private setupEvents(): void {
    this.formGroup.controls.Interval?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(_ => {
        this.intervalElements = [
          ...this.dataFilterElementsService.toggleIntervalFieldsVisibility(
            this.intervalElements,
            this.formGroup
          ),
        ];
      });
  }

  private openFillChangesDialog(): void {
    if (!this.dialogService.getOpenDialogCount()) {
      this.dialog
        .open({
          size: 'md',
          type: 'warning',
          title: 'General.UpsertNany.Title',
          contentText: 'General.UpsertNany.Text',
          confirmText: 'General.Save',
          cancelText: 'General.Discard',
        })
        .closed.pipe(take(1))
        .subscribe(({ source }) => {
          if (source === 'confirm') {
            let isDriverCopy = false;
            const payload = this.fillableTrips.map(trip => {
              if (trip.Username) {
                isDriverCopy = true;
                delete trip.Username;
                return trip;
              }
              return trip;
            });
            this.updateTrips(payload, isDriverCopy);
          } else {
            this.fillableTrips.forEach(trip => {
              this.gridApi
                ?.getRowNode(String(trip.TripID))
                ?.setDataValue(
                  trip.Username ? 'Username' : 'Purpose',
                  undefined
                );
            });
          }
          this.fillableTrips = [];
        });
    }
  }

  private fillOperationFn(params: FillOperationParams): void {
    /* save tripID's that had empty values before fill
    (valid only for purpose. Username gets overriden all the time) */
    const colId = params.column.getColId();
    if (colId === 'Username' || !params.currentCellValue) {
      switch (params.column.getColId()) {
        case 'Purpose':
          this.fillableTrips.push({
            TripID: params.rowNode.data.TripID,
            Purpose: params.initialValues[0],
          });
          break;
        case 'Username':
          params.rowNode.setDataValue('Username', params.initialValues[0]);
          this.fillableTrips.push({
            TripID: params.rowNode.data.TripID,
            Username: params.initialValues[0],
            UserID: Number(this.selectedNode?.data?.UserID),
          });
          break;
      }
    }
    if (this.fillableTrips.length) {
      this.openFillChangesDialog();
    }
    return params.currentCellValue
      ? params.currentCellValue
      : params.initialValues[0];
  }

  private updateTrips(payload: object[], isDriverCopy: boolean): void {
    this.api
      .upsertMany<TripUpsertManyResp>(Entity.Trip, payload)
      .pipe(
        catchError(() => {
          isDriverCopy
            ? this.resetDriverData(payload)
            : this.resetPurposeData(payload);
          return of();
        })
      )
      .subscribe(({ data }) => {
        const failedTrips = data.filter(
          item => item.Status === UpsertManyItemStatus.Fail
        );
        const succededTrips = data.filter(
          item => item.Status === UpsertManyItemStatus.Ok
        );

        isDriverCopy
          ? this.resetDriverData(failedTrips)
          : this.resetPurposeData(failedTrips);

        if (isDriverCopy) {
          succededTrips.forEach(tripsaveSuccessResp => {
            this.gridApi
              ?.getRowNode(String(tripsaveSuccessResp.TripID))
              ?.setDataValue(
                'UserID',
                tripsaveSuccessResp.UpdatedRowData.UserID
              );
          });
        }
      });
  }

  private resetDriverData(itemsToReset: object[]): void {
    itemsToReset.forEach(item => {
      this.gridApi
        ?.getRowNode(String((item as TripDto).TripID))
        ?.setDataValue('Username', undefined);
    });
  }

  private resetPurposeData(itemsToReset: object[]): void {
    itemsToReset.forEach(item => {
      this.gridApi
        ?.getRowNode(String((item as TripDto).TripID))
        ?.setDataValue('Purpose', undefined);
    });
  }

  private subscribeToGridDataLoaded(context: GridContext<TripDto>): void {
    context.gridDataLoaded$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.gridApi) this.selectUrlRowOrFirst(this.gridApi);
      });
  }
}
